import type { System } from '@/core/system';
import type { Engine } from '@/core/engine';
import type { Time } from '@/core/time';
import { City, CellKind } from '@/world/city';
import { Ground } from '@/rendering/ground';
import { NARRATIVE_BEATS } from '@/config';

/**
 * The story of Palimpsest is told almost entirely through the world itself; this
 * controller is the thin thread of *words* laid over that. It watches the city's
 * maturity — the accumulated weight of everything it has built for you — and, at a
 * few thresholds, lets a single quiet line surface. The final beat delivers the
 * thesis the whole experience has been building toward:
 *
 *     "this city was never a place. it was a mirror."
 *
 * It emits beats as events; the UI decides how to render them. Beats fire once and
 * never repeat.
 */
export class NarrativeController implements System {
  readonly name = 'narrative';

  private fired = new Set<string>();
  private builtFinale = false;

  constructor(
    private engine: Engine,
    private city: City,
    /** Optional so main.ts compiles before Fable wires the finale ripple in. */
    private ground?: Ground,
  ) {}

  update(_time: Time): void {
    const m = this.city.maturity;
    for (const beat of NARRATIVE_BEATS) {
      if (m >= beat.at && !this.fired.has(beat.id)) {
        this.fired.add(beat.id);
        this.engine.events.emit('narrative:beat', {
          id: beat.id,
          text: beat.text,
          progress: m,
        });
        // The finale beat doesn't just speak — the city physically answers.
        if (beat.id === 'finale') this.buildFinale();
      }
    }
  }

  /**
   * The ending, made of matter: at the exact centre of the city the Mirror
   * Monolith grows — a lone pale spire — ringed by beacons with still pools between
   * them, and one large ink-ripple washes outward from it. The beat's line ("you
   * may keep walking / it will keep listening") serves as the epilogue over it.
   */
  private buildFinale(): void {
    if (this.builtFinale) return;
    this.builtFinale = true;

    const cc = Math.round(this.city.centre);
    // Clear the centre cell and dissolve anything already standing on it.
    for (const b of this.city.buildings) {
      if (!b.dying && b.cx === cc && b.cz === cc) b.dying = true;
    }
    this.city.setKind(cc, cc, CellKind.Block);

    // The monolith itself — a slender near-white spire, ~80m tall on a 3×3 footprint.
    this.city.addBuilding({
      x: 0, z: 0,
      width: 3, depth: 3,
      yaw: 0,
      height: 80,
      tone: 1,
      seed: 42,
      style: 'spire',
      cx: cc, cz: cc,
      bornAt: 0,
    });

    // A ring of 8 beacons at r≈10, with 4 still pools set between every other pair.
    const beacons = 8;
    const radius = 10;
    for (let i = 0; i < beacons; i++) {
      const a = (i / beacons) * Math.PI * 2;
      this.city.addOrnament({
        kind: 'beacon',
        x: Math.cos(a) * radius, y: 0, z: Math.sin(a) * radius,
        yaw: 0, scale: 1.2, seed: i * 37,
      });
    }
    for (let i = 0; i < 4; i++) {
      const a = ((i + 0.5) / 4) * Math.PI * 2;
      this.city.addOrnament({
        kind: 'pool',
        x: Math.cos(a) * radius, y: 0.02, z: Math.sin(a) * radius,
        yaw: 0, scale: 1.1, seed: 100 + i * 13,
      });
    }

    // The ground answers with one deep ripple from the centre.
    this.ground?.syncFromCity(this.city);
    this.ground?.ripple(0, 0, 2.5);
  }

  /** For the save system. */
  getFired(): string[] {
    return [...this.fired];
  }
  /** Whether a given beat has already fired — used by the FinaleDirector to detect
   *  an already-seen ceremony on boot and skip straight to the epilogue. */
  hasFired(id: string): boolean {
    return this.fired.has(id);
  }
  restore(ids: string[]): void {
    this.fired = new Set(ids);
  }
}
